import { useCallback, useEffect, useRef, useState } from 'react';

import {
  geolocationPermissionDeniedMessage,
  isIosDevice,
} from '../utils/geolocation-platform';

const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 5000,
  timeout: 20000,
};

const FRESH_GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 15000,
};

export function getFreshGeolocationPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('La géolocalisation n’est pas disponible sur cet appareil.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, FRESH_GEO_OPTIONS);
  });
}

function geolocationErrorMessage(error: GeolocationPositionError): string {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return geolocationPermissionDeniedMessage();
    case error.POSITION_UNAVAILABLE:
      return 'Signal GPS indisponible. Vérifiez que le GPS est activé sur votre téléphone.';
    case error.TIMEOUT:
      return 'Position introuvable pour le moment. Réessayez à l’extérieur ou près d’une fenêtre.';
    default:
      return 'Impossible d’obtenir votre position.';
  }
}

type GeolocationStatus = 'idle' | 'requesting' | 'active' | 'error';

export function useGeolocation() {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [status, setStatus] = useState<GeolocationStatus>('idle');
  const watchIdRef = useRef<number | null>(null);
  const isRequestingRef = useRef(false);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const handleError = useCallback((error: GeolocationPositionError) => {
    isRequestingRef.current = false;
    setStatus('error');
    setGeoError(geolocationErrorMessage(error));
  }, []);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus('error');
      setGeoError('La géolocalisation n’est pas disponible sur cet appareil.');
      return;
    }

    if (isRequestingRef.current) {
      return;
    }

    isRequestingRef.current = true;
    setGeoError(null);
    setStatus('requesting');
    stopWatch();

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        isRequestingRef.current = false;
        setPosition(pos);
        setStatus('active');
        setGeoError(null);
        watchIdRef.current = navigator.geolocation.watchPosition(
          (nextPos) => {
            setPosition(nextPos);
            setGeoError(null);
            setStatus('active');
          },
          handleError,
          GEO_OPTIONS,
        );
      },
      handleError,
      { ...GEO_OPTIONS, maximumAge: 0 },
    );
  }, [handleError, stopWatch]);

  useEffect(() => {
    if (isIosDevice() || !navigator.geolocation || !navigator.permissions?.query) {
      return;
    }

    void navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (result.state === 'granted') {
          startTracking();
        }
      })
      .catch(() => undefined);
  }, [startTracking]);

  useEffect(() => () => stopWatch(), [stopWatch]);

  const needsPermission =
    status === 'idle' || (status === 'error' && !position);

  return {
    position,
    geoError,
    startTracking,
    needsPermission,
    isRequesting: status === 'requesting',
  };
}
